import {createSelector} from 'reselect';

import appConfig from '@/config/appConfig.typed';
import * as iccConsumerAggregatedAttributeTypes from '@/consts/icc/iccConsumerAggregatedAttributeTypes';
import {normalizeMemberJourneys} from '@/services/loyalty/journeyNormalizer';
import {
    getEarnedBadgeIds,
    hasCompletedBadgeActions,
    hasStartedBadgeActions,
    isBadgeAction,
    isJourneyActionCompleted,
    isJourneyCompleted,
    isJourneyInProgress,
    isJourneyLevelCompleted,
    isJourneyLevelInProgress,
    withResolvedBadgeActions,
} from '@/services/loyalty/loyaltyUtils';

import {makeSelectConsumerAggregatedAttribute, selectConsumer} from './consumer';

const CLUB_PLUS_SUBSCRIPTION_ACTIVE = 'active';

const selectIsLoyaltyMember = createSelector(
    makeSelectConsumerAggregatedAttribute(iccConsumerAggregatedAttributeTypes.IS_LOYALTY_MEMBER),
    (value) => {
        return value?.toLowerCase() === 'true';
    }
);

const selectLoyaltyLevel = createSelector(
    makeSelectConsumerAggregatedAttribute(iccConsumerAggregatedAttributeTypes.LOYALTY_LEVEL),
    (value) => {
        const loyaltyLevels = appConfig.getLoyaltyLevels();

        if (!loyaltyLevels?.length || !value) return;

        const level = loyaltyLevels.find((x) => x.loyaltyKey?.toLowerCase() === value.toLowerCase());

        if (!level) return;

        const nextLevel = loyaltyLevels.find((x) => x.order === level.order + 1);

        return {...level, nextLevel};
    }
);

const selectLoyaltyPoints = createSelector(
    makeSelectConsumerAggregatedAttribute(iccConsumerAggregatedAttributeTypes.LOYALTY_POINTS),
    (value) => {
        return parseInt(value) || 0;
    }
);

const selectLoyaltyJourneys = createSelector(selectConsumer, (state) => state.loyaltyJourneys);

const selectIsLoyaltyJourneysFetched = createSelector(selectLoyaltyJourneys, (loyalty) => loyalty !== undefined);

const selectIsClubPlusSubscriber = createSelector(
    selectLoyaltyJourneys,
    (loyalty) => loyalty?.data?.subscriptionStatus?.toLowerCase() === CLUB_PLUS_SUBSCRIPTION_ACTIVE
);

const selectMemberJourneys = createSelector(selectLoyaltyJourneys, (loyalty) => {
    return normalizeMemberJourneys(loyalty?.data?.memberJourneys);
});

const selectActiveJourneys = createSelector(selectLoyaltyJourneys, selectMemberJourneys, (loyalty, memberJourneys) => {
    const memberJourneyIds = new Set(memberJourneys.map(({journeyId}) => journeyId));
    const earnedBadgeIds = getEarnedBadgeIds(memberJourneys);

    return (loyalty?.data?.journeys || [])
        .filter(({journeyId}) => !memberJourneyIds.has(journeyId))
        .map((journey) => withResolvedBadgeActions(journey, earnedBadgeIds));
});

const selectAllJourneys = createSelector(
    selectMemberJourneys,
    selectActiveJourneys,
    (memberJourneys, activeJourneys) => [...memberJourneys, ...activeJourneys]
);

const selectAllExpiringJourneys = createSelector(selectAllJourneys, (journeys) => {
    return journeys.filter((journey) => {
        if (isJourneyCompleted(journey)) {
            return false;
        }

        const expireAt = journey?.journeyExpireAt || journey?.memberJourneyExpireAt;

        if (!expireAt) {
            return false;
        }

        const expireDate = new Date(expireAt);
        const currentDate = new Date();
        const timeDiff = expireDate - currentDate;

        return timeDiff > 0;
    });
});
// A catalogue journey whose required badges are all earned counts as completed too - the backend
// leaves it "Active" instead of moving it into memberJourneys (Legend badge).
const selectCompletedJourneys = createSelector(
    selectMemberJourneys,
    selectActiveJourneys,
    (memberJourneys, activeJourneys) => [
        ...memberJourneys.filter(isJourneyCompleted),
        ...activeJourneys.filter(hasCompletedBadgeActions),
    ]
);

const selectInProgressJourneys = createSelector(
    selectMemberJourneys,
    selectActiveJourneys,
    (memberJourneys, activeJourneys) => [
        ...memberJourneys.filter(isJourneyInProgress),
        ...activeJourneys.filter((journey) => hasStartedBadgeActions(journey) && !hasCompletedBadgeActions(journey)),
    ]
);

const selectAvailableJourneys = createSelector(selectActiveJourneys, (activeJourneys) => {
    return activeJourneys.filter((journey) => !hasCompletedBadgeActions(journey));
});

const selectAllJourneyLevels = createSelector(selectMemberJourneys, (memberJourneys) => {
    return memberJourneys.flatMap((journey) => journey?.journeyLevels).filter(Boolean);
});

const selectStartedJourneyLevels = createSelector(selectAllJourneyLevels, (journeyLevels) => {
    const startedLevelsArray = journeyLevels?.filter(isJourneyLevelInProgress)?.filter((level) => level?.journeyBadge);

    return startedLevelsArray;
});

const selectCompletedJourneyLevels = createSelector(selectAllJourneyLevels, (journeyLevels) => {
    const startedLevelsArray = journeyLevels?.filter(isJourneyLevelCompleted);

    return startedLevelsArray;
});

const getBadgesFromJourneyLevels = (journeyLevels) => {
    const badges = journeyLevels?.filter((level) => level?.journeyBadge).map((level) => level?.journeyBadge);

    return badges;
};

const selectAllBadges = createSelector(selectAllJourneyLevels, (journeyLevels) => {
    const badges = getBadgesFromJourneyLevels(journeyLevels);

    return badges;
});

const selectAllActions = createSelector(selectAllJourneyLevels, (journeyLevels) => {
    const actions = [];

    journeyLevels?.forEach((level) => {
        const levelActions = level?.journeyActions?.filter((action) => !isBadgeAction(action));

        levelActions?.forEach((action) => {
            actions.push({...action, journeyBadge: level.journeyBadge, levelActions});
        });
    });

    return actions;
});

const selectCompletedActions = createSelector(selectAllActions, (actions) => {
    return actions.filter(isJourneyActionCompleted);
});

const selectNotCompletedActions = createSelector(selectAllActions, (actions) => {
    return actions.filter((action) => !isJourneyActionCompleted(action));
});

export {
    selectAllActions,
    selectAllBadges,
    selectAllExpiringJourneys,
    selectAllJourneyLevels,
    selectAllJourneys,
    selectAvailableJourneys,
    selectCompletedActions,
    selectCompletedJourneyLevels,
    selectCompletedJourneys,
    selectInProgressJourneys,
    selectIsClubPlusSubscriber,
    selectIsLoyaltyJourneysFetched,
    selectIsLoyaltyMember,
    selectLoyaltyJourneys,
    selectLoyaltyLevel,
    selectLoyaltyPoints,
    selectMemberJourneys,
    selectNotCompletedActions,
    selectStartedJourneyLevels,
};
